import React, { useMemo } from 'react';
import { useParams } from 'umi';
import moment from 'moment';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import {
  TECHLAND_GAME_CODE,
  TECHLAND_STUDIO_CODE,
} from '@home/pages/studio/home/_constant';
import { computeStudioCardTrendStartMoment } from '@home/pages/studio/home/_trendUtils';
import {
  applySpecialDealsFlag,
  mergeSpecialDealsTrackData,
} from '@home/pages/studio/home/utils/specialDealsRequest';

export interface IStudioCardTrendCompleteMap {
  day?: number;
  week?: number;
  month?: number;
}

/**
 * Scope 切换时：独立 dataKey、请求 metrics；
 * cardProps 会合并进子级 PageContainer `Render` 的 `config.props`（即 StudioCardHorBar 的最终 props），
 * 且覆盖 pageConfig 里 HorBar 同名字段，避免 Render 中 {...resetProps}{...cprops} 顺序导致注入失效。
 */
export interface IStudioCardTrendScopeVariant {
  dataKey: string;
  metrics: string[];
  /** card_metrics 等接口可选：主 metrics 缺数时的回退指标 */
  fallback_metrics?: string[];
  /** 写入 StudioCardHorBar：metricKey、seriesConfig、formatRule 等；建议在此维护全量 seriesConfig */
  cardProps?: Record<string, unknown>;
}

export type TStudioCardTrendScopeVariants = Record<string, IStudioCardTrendScopeVariant>;

/** 将 trackData 中的 filterInitData 与页面静态 extra 合并为接口 filters；同 key 时 extraFilters 覆盖前者 */
function mergeStudioCardTrendFilters(
  filterInitData: unknown,
  extraFilters: unknown,
): Record<string, unknown> {
  const base =
    filterInitData &&
      typeof filterInitData === 'object' &&
      !Array.isArray(filterInitData)
      ? { ...(filterInitData as Record<string, unknown>) }
      : {};
  const extra =
    extraFilters &&
      typeof extraFilters === 'object' &&
      !Array.isArray(extraFilters)
      ? (extraFilters as Record<string, unknown>)
      : {};
  return { ...base, ...extra };
}

type TRenderChildProps = {
  config?: { name?: string; props?: Record<string, unknown>;[key: string]: unknown; };
  [key: string]: unknown;
};

/** 将 scope 的 cardProps 合并进下一层 Render(StudioCardHorBar) 的 config.props，并写入 dataKey */
function mergeScopeIntoStudioCardHorBarRender(
  nodes: React.ReactNode,
  overlay: Record<string, unknown> | undefined,
  dataKeyVal: string,
): React.ReactNode {
  if (!overlay || typeof overlay !== 'object' || Object.keys(overlay).length === 0) {
    return nodes;
  }
  return React.Children.map(nodes, child => {
    if (!React.isValidElement(child)) {
      return child;
    }
    const p = child.props as TRenderChildProps;
    const cfg = p.config;
    if (cfg?.name !== 'StudioCardHorBar') {
      return child;
    }
    return React.cloneElement(child, {
      ...p,
      config: {
        ...cfg,
        props: {
          ...(cfg.props || {}),
          ...overlay,
          dataKey: dataKeyVal,
        },
      },
    } satisfies typeof child.props);
  });
}

export interface IStudioCardTrendServiceProps {
  api: string;
  dataKey?: string;
  gameCode?: string;
  /** 与移动端一致：日/周/月各取最近 N 个周期 */
  completeMap?: IStudioCardTrendCompleteMap;
  metrics?: string[];
  /**
   * 按筛选 Scope（如 net_revenue / gross_revenue）切换请求与卡片配置；
   * 若传入则忽略顶层 dataKey、metrics，以各 variant 为准。
   */
  scopeVariants?: TStudioCardTrendScopeVariants;
  /**
   * 与 filter.scope 及 scopeVariants 的 key 对齐；
   * 当 scope 未命中任一 variant 时回退到该 key（须存在于 scopeVariants）。
   */
  scopeDefaultKey?: string;
  /**
   * 与 filterInitData 合并后写入请求体 filters（如 category_list / product_list）。
   * 同名字段以 extraFilters 为准。
   */
  extraFilters?: Record<string, unknown>;
  trackData?: string[];
  filterLoading?: boolean;
  data?: Record<string, unknown>;
  filter?: Record<string, unknown>;
  filterVal?: Record<string, unknown>;
  children?: React.ReactElement | React.ReactElement[];
  className?: string;
  style?: React.CSSProperties;
  formatData?: (res: any, filterVal?: any) => any;
  /** 透传给 Service，开关打开时在请求中追加 special deals logical_flag */
  appendSpecialDealsFlag?: boolean;
}

/**
 * Techland Studio Home — Revenue Breakdown 等横向柱状图：请求 card_metrics，补齐 trend 时间窗。
 */
export default function StudioCardTrendService(props: IStudioCardTrendServiceProps) {
  const {
    api,
    dataKey: dataKeyProp = 'studioCardHorBar',
    gameCode = TECHLAND_GAME_CODE,
    completeMap = { day: 5, week: 5, month: 5 },
    metrics: metricsProp = ['profit', 'budget_revenue', 'plan_revenue'],
    scopeVariants,
    scopeDefaultKey,
    extraFilters,
    trackData = ['filterInitData'],
    filterLoading: propsFilterLoading,
    data: propsData,
    filter: filterFromParent,
    filterVal: filterValFromParent,
    children,
    className,
    style,
    formatData: formatDataProp,
    appendSpecialDealsFlag,
  } = props;

  const { studioId } = useParams() as { studioId?: string; };
  const studioCode = studioId || TECHLAND_STUDIO_CODE;

  const serviceData = useMemo(
    () => ({
      ...(propsData ?? {}),
      filter:
        filterFromParent ??
        filterValFromParent ??
        (propsData as { filter?: Record<string, unknown>; } | undefined)?.filter,
    }),
    [propsData, filterFromParent, filterValFromParent],
  );

  const filterScope = (
    serviceData.filter as { scope?: string; } | undefined
  )?.scope;

  const { activeScopeKey, variant } = useMemo(() => {
    if (!scopeVariants) {
      return { activeScopeKey: filterScope ?? '', variant: undefined };
    }
    let key = filterScope ?? '';
    if (!key || !scopeVariants[key]) {
      const fallback =
        scopeDefaultKey && scopeVariants[scopeDefaultKey]
          ? scopeDefaultKey
          : Object.keys(scopeVariants)[0];
      key = fallback ?? '';
    }
    return { activeScopeKey: key, variant: scopeVariants[key] };
  }, [scopeVariants, scopeDefaultKey, filterScope]);

  const dataKey = variant?.dataKey ?? dataKeyProp;
  const metrics = variant?.metrics ?? metricsProp;
  const fallbackMetrics = variant?.fallback_metrics;

  const beforeRequest = (reqData: any) => {
    const fv = reqData?.filterVal ?? filterValFromParent;
    const filterInitData = reqData?.filterInitData ?? {};
    const endRaw = fv?.end_time;
    if (!endRaw) {
      return false;
    }
    const period = (fv?.date_type as string) ?? 'daily';

    const mEnd = moment(String(endRaw).slice(0, 10), 'YYYY-MM-DD');
    if (!mEnd.isValid()) {
      return false;
    }

    const mStart = computeStudioCardTrendStartMoment(mEnd, period, completeMap);

    const apiMetrics = metrics;

    const params = {
      game_code: gameCode,
      studio_code: studioCode,
      start_time: mStart.format('YYYY-MM-DD'),
      end_time: mEnd.format('YYYY-MM-DD'),
      period,
      metrics: apiMetrics,
      trend_metrics: apiMetrics,
      ...(fallbackMetrics?.length ? { fallback_metrics: fallbackMetrics } : {}),
      filters: mergeStudioCardTrendFilters(filterInitData, extraFilters),
      is_confirm: false,
    };
    return applySpecialDealsFlag(params, reqData, appendSpecialDealsFlag);
  };

  const effectiveTrackData = useMemo(
    () => mergeSpecialDealsTrackData(trackData, appendSpecialDealsFlag),
    [trackData, appendSpecialDealsFlag],
  );

  const formatData = (res: any, filterVal: any) => {
    if (formatDataProp) {
      return formatDataProp(res, filterVal);
    }
    return res;
  };

  const mergedChildren = useMemo(
    () => mergeScopeIntoStudioCardHorBarRender(children, variant?.cardProps, dataKey),
    [children, variant?.cardProps, dataKey],
  );

  const content = (
    <Service
      key={`${dataKey}-${activeScopeKey || 'default'}`}
      api={api}
      dataKey={dataKey}
      beforeRequest={beforeRequest}
      formatData={formatData}
      rawData
      trackData={effectiveTrackData}
      data={serviceData}
      filterLoading={propsFilterLoading}
    >
      {mergedChildren}
    </Service>
  );

  return (
    <div className={className} style={style}>
      {content}
    </div>
  );
}
